import useAppDispatch from "@/hooks/useAppDispatch";
import useAppSelector from "@/hooks/useAppSelector";
import useModalAuth from "@/hooks/useModalAuth";
import {
  COUNTRYCODE,
  GOOGLE_MAP_LIBRARIES,
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_ID,
} from "@/recourses/constants";
import { genericsAction } from "@/store/generics";
import { cartStoresActions } from "@/store/cartStore";
import { popupsRemunderAction } from "@/store/PopUpsReminderSlice";
import { useJsApiLoader } from "@react-google-maps/api";
import { useCallback, useEffect, useState } from "react";
import useAddressModal from "@/hooks/useAddressModal";
import useScrollPosition from "@/hooks/useScrollPosition";
import { pageView } from "@/utils/analyticsV2";
import { useLocation, useMatch } from "react-router-dom";
import useAnalytics from "@/hooks/useAnalytics";
import useModalHearAboutUs from "@/hooks/useModalHearAboutUs";
import useQueryParams from "@/hooks/useQueryParams";
import { getCities } from "@/utils/CountriesConfig";
import { textNormalize } from "@/utils/textNormalize";
import { selectProcessedItems, selectTotal } from "@/store/checkout/selector";
import { EStoreReview } from "@/components/Molecules/ModalStoreReview";

const cities = getCities(COUNTRYCODE);

export default function useRootLayout() {
  const dispatch = useAppDispatch();
  const { queryParams, setQueryParams } = useQueryParams();
  const { handleCloseModalAuth, isOpen: isAuthModalOpen } = useModalAuth();
  const { openModalHearAboutUs, handleCloseModalHearAboutUs, type } = useModalHearAboutUs();

  const storeId = useAppSelector((state) => state.cart.storeId);
  const lastSilentStoreId = useAppSelector((state) => state.cart.lastSilentStoreId);
  const items = useAppSelector(selectProcessedItems);
  const total = useAppSelector(selectTotal);
  const showLocationModal = useAppSelector((state) => state.popUpsReminder.showLocationModal);
  const [oldStoreId, setOldStoreId] = useState<number | undefined>(storeId);
  const { pathname } = useLocation();
  const { pageView: pageViewHook } = useAnalytics();
  const [resetScroll, setResetScroll] = useState(false);
  const [openModalStoreReview, setOpenModalStoreReview] = useState(false);
  const [storeReview, setStoreReview] = useState<EStoreReview | undefined>();
  const [region, setRegion] = useState<CityType | Omit<LocationType, "test" | "isPickUp"> | null>(null);

  const listStores = cities?.filter((city) => city.is_available || city.is_new);
  const regionId = queryParams?.get("region_id");

  const {
    isOpen: isAddressModalOpen,
    toggleAddressModal: setIsAddressModalOpen,
    handleChangeLocation,
  } = useAddressModal();

  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_ID,
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAP_LIBRARIES,
    language: "es",
    region: "CO",
  });

  const handleCloseModalInformativeChangeLocation = () => {
    setOldStoreId(storeId);
  };

  useEffect(() => {
    dispatch(genericsAction.setIsLoadedScriptGoogleMaps({ isLoaded, loadError }));
  }, [isLoaded]);

  useEffect(() => {
    if (region?.lat) return;
    setOldStoreId(storeId);
  }, [storeId !== undefined, region?.lat]);

  useEffect(() => {
    pageView.eventLocation();
    pageView.eventTiming();
  }, []);

  useEffect(() => {
    const rating = queryParams?.get("rating")?.toLowerCase();

    const enumValues = Object.values(EStoreReview);
    if (rating && enumValues.includes(rating as EStoreReview)) {
      setOpenModalStoreReview(true);
      setStoreReview(rating as EStoreReview);
    }

    setQueryParams({ rating: null });
  }, [queryParams?.get("rating")]);

  const handleReset = () => {
    setResetScroll((prev) => !prev); // Cambia el valor de resetScroll para activar el reset
  };

  const checkPageCategory = () => {
    pageViewHook.eventPageCategory();
    handleReset();
  };

  useEffect(() => {
    // Search region id (google) on location and set region
    if (regionId) {
      const region =
        listStores?.find((city) => textNormalize(city?.region_id ?? "") === textNormalize(regionId)) ?? null;

      setRegion(region);

      if (region) {
        void handleChangeLocation(region).then(() => setQueryParams({ region_id: null }));
      } else {
        setQueryParams({ region_id: null });
      }
    }
  }, [regionId]);

  // Reset location for offline stores
  useEffect(() => {
    if (storeId === 136) {
      dispatch(cartStoresActions.setInitialLocation());
      dispatch(cartStoresActions.emptyAvailability());
      dispatch(
        popupsRemunderAction.setPopUpsReminder({
          showLocationModal: true,
          showAddressReminder: false,
        })
      );
    }
  }, []);

  useEffect(() => {
    if (showLocationModal) {
      setIsAddressModalOpen(true);
    }
  }, [showLocationModal]);

  useEffect(() => {
    // 2- History Change event
    void pageView.eventPathname({ pathname, description: pathname, items, value: total });

    // 3- Event with information aditional
    checkPageCategory();
  }, [pathname]);

  useScrollPosition({
    threshold: 25,
    callback: () => pageView.eventPageScroll(25),
    reset: resetScroll,
  });
  useScrollPosition({
    threshold: 50,
    callback: () => pageView.eventPageScroll(50),
    reset: resetScroll,
  });
  useScrollPosition({
    threshold: 75,
    callback: () => pageView.eventPageScroll(75),
    reset: resetScroll,
  });
  useScrollPosition({
    threshold: 100,
    callback: () => pageView.eventPageScroll(100),
    reset: resetScroll,
  });

  return {
    handleCloseModalAuth,
    isAuthModalOpen,
    oldStoreId,
    storeId,
    // Only for explicit user changes
    isOpenModalInformativeChangeLocation:
      storeId !== undefined &&
      storeId !== oldStoreId &&
      storeId !== lastSilentStoreId &&
      !showLocationModal &&
      !region,
    isAddressModalOpen: isAddressModalOpen && !region,
    handleCloseModalInformativeChangeLocation,
    setIsAddressModalOpen,
    openModalHearAboutUs,
    handleCloseModalHearAboutUs,
    typeModalHearAboutUs: type,
    openModalStoreReview,
    setOpenModalStoreReview,
    storeReview,
  };
}
